// app/javascript/components/ShippingFlow/hooks/useOnBehalf.js
import { useState, useCallback } from 'react';

/**
 * Custom hook for managing "On Behalf Of" user functionality
 * Handles searching for existing users and managing selected user state
 */
const useOnBehalf = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onBehalfUser, setOnBehalfUser] = useState(null);

  /**
   * Search for a user by email address
   * @param {string} email - User email to search for
   * @returns {Promise<Object>} User object if found
   */
  const searchUserByEmail = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        'user[email]': email
      });
      
      const response = await fetch(`/users/search_user?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(window.gon?.csrf_token && { 'X-CSRF-Token': window.gon.csrf_token })
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'User not found' }));
        throw new Error(errorData.error || 'User not found');
      }
      
      const data = await response.json();
      
      if (!data.success || !data.user) {
        throw new Error('User not found');
      }
      
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Search for a pro club member
   * Note: This endpoint may need to be created on the backend
   * @param {string} lookup - Pro club lookup identifier
   * @returns {Promise<Object>} Pro user object if found
   */
  const searchProUser = useCallback(async (lookup) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        'pro_lookup': lookup
      });
      
      const response = await fetch(`/users/search_pro?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(window.gon?.csrf_token && { 'X-CSRF-Token': window.gon.csrf_token })
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Pro user not found' }));
        throw new Error(errorData.error || 'Pro user not found');
      }
      
      const data = await response.json();
      
      if (!data.success || !data.user) {
        throw new Error('Pro user not found');
      }
      
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Set the on-behalf-of user
   * @param {Object} user - User object to set
   */
  const setUser = useCallback((user) => {
    setOnBehalfUser(user);
    setError(null);
  }, []);

  /**
   * Clear the on-behalf-of user
   */
  const clearUser = useCallback(() => {
    setOnBehalfUser(null);
    setError(null);
  }, []);

  /**
   * Format user for display
   * @param {Object} user - User object
   * @returns {string} Formatted user display string
   */
  const formatUserDisplay = useCallback((user) => {
    if (!user) return '';
    
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const email = user.email || '';
    
    if (name && email) {
      return `${name} (${email})`;
    }
    
    return email || name || 'Unknown User';
  }, []);

  return {
    loading,
    error,
    onBehalfUser,
    setOnBehalfUser: setUser,
    clearOnBehalfUser: clearUser,
    searchUserByEmail,
    searchProUser,
    formatUserDisplay
  };
};

export default useOnBehalf;
